import InsightsTooltip from 'components/ReusableComponents/Charts/Inslghts/InsightsTooltip'
import DataIsReprocessingBar from 'components/ReusableComponents/DataIsReprocessingBar'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import renderYearSeperators from 'components/ReusableComponents/YearSeperatorsForCharts'
import { usePerformanceReportsDates } from 'features/Analytics'
import AnalyticTabs from 'features/Analytics/AnalyticTab'
import Diagnose from 'features/Diagnose'
import { barColors } from 'features/Engagement/engagementConstants'
import useAllHotelsToggle from 'hooks/useAllHotels'
import moment from 'moment'
import { useEffect, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'
import performanceReportsApi from 'services/api/performanceReports'

import { provideEndDate } from '../provideEndDate'
import styles from './Reviews.module.scss'
import ShieldIcon from './ShieldIcon'
import TripAdvisorRating from './TripAdvisorRating'

const Reviews = () => {
  const { fromDate, setFromDate, toDate, setToDate } = usePerformanceReportsDates()

  const {
    showAllHotels,
    setShowAllHotels,
    showWholeGroup,
    setShowWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels
  } = useAllHotelsToggle()

  const [reviewsOverTime, setReviewsOverTime] = useState()
  const [rankingsOverTime, setRankingsOverTime] = useState()
  const [ratingsOvertTime, setRatingsOvertTime] = useState()

  const [TAstats, setTAstats] = useState({})

  const [reviewTripAdvisorPage, setReviewTripAdvisorPage] = useState()

  const [isLoading, setIsLoading] = useState(true)
  const [isErred, setIsErred] = useState(false)

  useEffect(() => {
    setIsLoading(true)

    let kind = 'CurrentSubsidiary'

    if (showAllHotels) {
      kind = 'AllSubsidiaries'
    }

    if (showWholeGroup) {
      kind = 'WholeGroup'
    }

    const abortController = new AbortController()

    performanceReportsApi
      .reviews({ fromDate, toDate: provideEndDate(toDate), kind }, abortController)
      .then(res => {
        const {
          latestRating,
          latestNumberOfReviews,
          latestAreaRank,
          latestAreaRankOutOf,
          deltaRating,
          deltaNumberOfReviews,
          deltaAreaRank,
          numberOfNegativeReviewsPrevented,
          ratingsSummary,
          rankingsSummary,
          newReviewsSummary,
          reviewTripAdvisorPage
        } = res

        setRatingsOvertTime(ratingsSummary)
        setRankingsOverTime(rankingsSummary)
        setReviewsOverTime(newReviewsSummary)
        setReviewTripAdvisorPage(reviewTripAdvisorPage)

        setTAstats({
          latestRating,
          latestNumberOfReviews,
          latestAreaRank,
          latestAreaRankOutOf,
          deltaRating,
          deltaNumberOfReviews,
          deltaAreaRank,
          numberOfNegativeReviewsPrevented
        })

        setIsErred(false)
        setIsLoading(false)
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setIsErred(true)
          setIsLoading(false)
        }
      })

    return () => abortController.abort()
  }, [fromDate, toDate, showAllHotels, showWholeGroup, idsOfCurrentGroup, idsOfAllHotels])

  return (
    <div>
      <AnalyticTabs
        showWholeGroup={showWholeGroup}
        setShowWholeGroup={setShowWholeGroup}
        setShowAllHotels={setShowAllHotels}
        showAllHotels={showAllHotels}
        setFromDate={setFromDate}
        setToDate={setToDate}
        fromDate={fromDate}
        toDate={toDate}
        hasGroupHotelControls={false}
      />
      <DataIsReprocessingBar />
      <Diagnose />
      {isErred && (
        <div className={styles.noResults}>
          <p>Something went wrong while getting outreach data</p>
        </div>
      )}
      {!isErred && (
        <div className={styles.wrapper}>
          <div className={styles.row}>
            <div>
              <TripAdvisorRating
                isLoading={isLoading}
                {...TAstats}
                reviewTripAdvisorPage={reviewTripAdvisorPage}
              />
              <div className={styles.negativeReviewsPreventedHolder}>
                {isLoading && <LoadingIndicator inline />}
                {!isLoading && (
                  <>
                    <ShieldIcon />
                    <div className={styles.negativeNumbersPrevetenedText}>
                      <p className={styles.negativeNumbersPrevetenedNumber}>
                        {TAstats.numberOfNegativeReviewsPrevented}
                      </p>
                      <p className={styles.negativeNumbersPrevetenedLabel}>
                        Negative reviews prevented
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className={styles.marketingMessagesHolder}>
              <h3 className={styles.title}>TripAdvisor rating </h3>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    width={500}
                    height={300}
                    data={ratingsOvertTime?.activities}
                    margin={{
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 15
                    }}
                  >
                    <CartesianGrid stroke="#F2F2F2" vertical={false} />
                    <XAxis
                      dataKey="date"
                      axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(0, 14)'
                      }}
                      tickFormatter={date => moment(date).format('MMM D')}
                      tickSize={0}
                      padding={{ top: 30, bottom: 20, right: 40 }}
                    />
                    <YAxis
                      tickSize={0}
                      axisLine={false}
                      domain={[0, 5]}
                      ticks={[0, 1.0, 2.0, 3.0, 4.0, 5.0]}
                      allowDecimals
                      yAxisId="left"
                      minTickGap={0.5}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(-10, 0)'
                      }}
                      padding={{ top: 40, bottom: 0 }}
                    />
                    <Tooltip
                      content={
                        <InsightsTooltip
                          labelFormatter={date => moment(date).format('MMM D, YYYY')}
                          dataTitle="Rating"
                          valueFormatter={rating => Number(rating).toFixed(1)}
                        />
                      }
                      cursor={{
                        stroke: 'red',
                        strokeWidth: 0,
                        fill: '#dedede',
                        opacity: 0.22
                      }}
                    />
                    {renderYearSeperators(ratingsOvertTime?.activities)}
                    <Line
                      dataKey="count"
                      stroke="#10B981"
                      type="monotone"
                      dot={false}
                      strokeWidth={2}
                      yAxisId="left"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.marketingMessagesHolder}>
              <h3 className={styles.title}>TripAdvisor ranking in area</h3>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    width={500}
                    height={300}
                    data={rankingsOverTime?.activities}
                    margin={{
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 15
                    }}
                  >
                    <CartesianGrid stroke="#F2F2F2" vertical={false} />
                    <XAxis
                      dataKey="date"
                      axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(0, 14)'
                      }}
                      tickFormatter={date => moment(date).format('MMM D')}
                      tickSize={0}
                      padding={{ top: 30, bottom: 20, right: 40 }}
                    />
                    <YAxis
                      tickSize={0}
                      axisLine={false}
                      domain={['dataMax', 1]}
                      reversed
                      minTickGap={1}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(-10, 0)'
                      }}
                      padding={{ top: 40, bottom: 0 }}
                      yAxisId="left"
                    />
                    <Tooltip
                      content={
                        <InsightsTooltip
                          labelFormatter={date => moment(date).format('MMM D, YYYY')}
                          dataTitle="Area rank"
                        />
                      }
                      cursor={{
                        stroke: 'red',
                        strokeWidth: 0,
                        fill: '#dedede',
                        opacity: 0.22
                      }}
                    />
                    {renderYearSeperators(rankingsOverTime?.activities)}
                    <Line
                      dataKey="count"
                      stroke="#10B981"
                      type="monotone"
                      dot={false}
                      strokeWidth={2}
                      yAxisId="left"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className={styles.marketingMessagesHolder}>
              <h3 className={styles.title}>New TripAdvisor reviews </h3>
              {isLoading && <LoadingIndicator inline />}
              {!isLoading && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    width={500}
                    height={300}
                    data={reviewsOverTime?.activities.map(({ date, count }) => ({
                      date,
                      count: count > 0 ? count : 0
                    }))}
                    margin={{
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 15
                    }}
                  >
                    <CartesianGrid stroke="#F2F2F2" vertical={false} />
                    <XAxis
                      dataKey="date"
                      axisLine={{ stroke: '#F2F2F2', strokeWidth: 1 }}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(0, 14)'
                      }}
                      tickFormatter={date => moment(date).format('MMM D')}
                      tickSize={0}
                      padding={{ top: 30, bottom: 20, right: 40 }}
                    />
                    <YAxis
                      tickSize={0}
                      axisLine={false}
                      allowDecimals={false}
                      tick={{
                        fill: '#7D7D7D',
                        fontSize: 12,
                        transform: 'translate(-10, 0)'
                      }}
                      padding={{ top: 40, bottom: 0 }}
                      range
                      yAxisId="left"
                    />
                    <Tooltip
                      content={
                        <InsightsTooltip
                          labelFormatter={date => moment(date).format('MMM D, YYYY')}
                          dataTitle="New reviews"
                        />
                      }
                      cursor={{
                        stroke: 'red',
                        strokeWidth: 0,
                        fill: '#dedede',
                        opacity: 0.22
                      }}
                    />
                    {renderYearSeperators(rankingsOverTime?.activities)}

                    <Bar
                      dataKey="count"
                      stackId="a"
                      fill={barColors[0]}
                      barSize={6}
                      yAxisId="left"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
          <div className={styles.row}></div>
        </div>
      )}
    </div>
  )
}

export default Reviews
